import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Eye, Plus, Save, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { LEVELS, SHOW_MODE_FEATURES, type GenericResponse, type LogLevel, type ShowMode } from "@/lib/types";

type Draft = {
  id: number | null;
  name: string;
  features: string[];
  header_title: string;
  header_subtitle: string;
  footer_text: string;
  min_level: LogLevel | "";
};

const EMPTY_DRAFT: Draft = {
  id: null,
  name: "",
  features: ["live_clock", "tiles", "event_feed"],
  header_title: "",
  header_subtitle: "",
  footer_text: "",
  min_level: "",
};

function fromShowMode(m: ShowMode): Draft {
  let features: string[] = [];
  try {
    const parsed = JSON.parse(m.features);
    if (Array.isArray(parsed)) features = parsed.filter((x) => typeof x === "string");
  } catch {
    // ignore
  }
  return {
    id: m.id,
    name: m.name,
    features,
    header_title: m.header_title ?? "",
    header_subtitle: m.header_subtitle ?? "",
    footer_text: m.footer_text ?? "",
    min_level: m.min_level ?? "",
  };
}

export function ShowModesPage() {
  const [modes, setModes] = useState<ShowMode[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get<{ modes: ShowMode[] }>("/api/showmodes");
      setModes(resp.modes ?? []);
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSave = async () => {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) {
      setFeedback({ kind: "error", text: t("Name ist Pflicht.") });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const body: Partial<ShowMode> & { name: string; features: string } = {
        id: editing.id ?? 0,
        name,
        features: JSON.stringify(editing.features),
        header_title: editing.header_title || null,
        header_subtitle: editing.header_subtitle || null,
        footer_text: editing.footer_text || null,
        min_level: editing.min_level === "" ? null : editing.min_level,
        created_at: new Date().toISOString(),
      };
      await api.post<GenericResponse>("/api/showmodes", body);
      setFeedback({ kind: "success", text: t("Show-Modus „{name}\" gespeichert.", { name }) });
      setEditing(null);
      await reload();
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (m: ShowMode) => {
    if (!confirm(t("Show-Modus „{name}\" wirklich löschen?", { name: m.name }))) return;
    try {
      await api.delete<GenericResponse>(`/api/showmodes/${m.id}`);
      setFeedback({ kind: "success", text: t("Show-Modus „{name}\" gelöscht.", { name: m.name }) });
      await reload();
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Löschen fehlgeschlagen") });
    }
  };

  const toggleFeature = (key: string) => {
    if (!editing) return;
    setEditing((d) =>
      d ? { ...d, features: d.features.includes(key) ? d.features.filter((f) => f !== key) : [...d.features, key] } : d,
    );
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("Show-Modi ({n})", { n: modes.length })}
        description={t("Konfigurationen für Wallboards. Aufruf über /anlagen/live/?showmode=<name>.")}
        actions={
          <button
            type="button"
            onClick={() => setEditing(EMPTY_DRAFT)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
          >
            <Plus className="h-3 w-3" /> {t("Neu")}
          </button>
        }
      >
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : modes.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t("Noch keine Show-Modi angelegt.")}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {modes.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                    {m.name}
                  </div>
                  {m.header_title ? (
                    <div className="text-[11px] text-muted-foreground">{m.header_title}</div>
                  ) : null}
                </div>
                <div className="flex items-center gap-1">
                  <a
                    href={`/anlagen/live/?showmode=${encodeURIComponent(m.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
                  >
                    <ExternalLink className="h-3 w-3" /> {t("Öffnen")}
                  </a>
                  <button
                    type="button"
                    onClick={() => setEditing(fromShowMode(m))}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
                  >
                    {t("Bearbeiten")}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(m)}
                    aria-label={t("Show-Modus {name} löschen", { name: m.name })}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {editing ? (
        <Card
          title={editing.id == null ? t("Neuer Show-Modus") : t("Show-Modus bearbeiten: {name}", { name: editing.name })}
          actions={
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
            >
              <X className="h-3 w-3" /> {t("Schließen")}
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <DraftField
              label={t("Name (URL-Slug)")}
              value={editing.name}
              onChange={(v) => setEditing({ ...editing, name: v })}
              placeholder={t("z.B. werk-sued-hauptbereich")}
              mono
            />
            <DraftField
              label={t("Header-Titel")}
              value={editing.header_title}
              onChange={(v) => setEditing({ ...editing, header_title: v })}
              placeholder={t("z.B. Werk Süd · Mandant ABC")}
            />
            <DraftField
              label={t("Header-Untertitel")}
              value={editing.header_subtitle}
              onChange={(v) => setEditing({ ...editing, header_subtitle: v })}
              placeholder={t("z.B. Live Anlagen-Status")}
            />
            <DraftField
              label={t("Footer-Watermark")}
              value={editing.footer_text}
              onChange={(v) => setEditing({ ...editing, footer_text: v })}
              placeholder={t("z.B. Powered by Needful Apps")}
            />
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Event-Feed Mindest-Level")}
              </span>
              <select
                value={editing.min_level}
                onChange={(e) =>
                  setEditing({ ...editing, min_level: e.target.value as Draft["min_level"] })
                }
                className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              >
                <option value="">{t("Alle Levels")}</option>
                {LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {t("ab {level}", { level: l })}
                  </option>
                ))}
              </select>
            </label>
            <div className="md:col-span-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Aktive Features")}
              </span>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SHOW_MODE_FEATURES.map((f) => {
                  const active = editing.features.includes(f.key);
                  return (
                    <label
                      key={f.key}
                      className={
                        "flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs " +
                        (active
                          ? "border-primary bg-accent text-accent-foreground"
                          : "border-border bg-card hover:bg-muted")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleFeature(f.key)}
                        className="h-3.5 w-3.5 cursor-pointer accent-primary"
                      />
                      <span>{f.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
            >
              <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Speichern")}
            </button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function DraftField({
  label,
  value,
  onChange,
  placeholder,
  mono = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={
          "h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary " +
          (mono ? "font-mono text-xs" : "")
        }
      />
    </label>
  );
}
